import _ from 'lodash';
import moment from 'moment';
import 'moment-timezone';

export const BACKEND_DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss.000[Z]';
export const BACKEND_DATE_TIME_FORMAT_TIMEZONED =
  'YYYY-MM-DD HH:mm:ssZ';
export const BACKEND_RESERVATION_INPUT_DATE_TIME_FORMAT =
  'YYYY-MM-DDTHH:mm:ss';

export const DURATION_SECONDS = {
  Minute: 60,
  Hour: 3600,
  Day: 86400,
  Week: 604800,
  Month: 2630016, // Average month duration of 30.44 days
  Year: 31557600, // Average year duration of 365.25 days
};

// UnixDateMin is epoch time in unix timestamp.
export const UnixDateMin = 0;

// UnixDateMin is end time in unix timestamp.
export const UnixDateMax = 2147483647;

// UnixMondayFirst is 01/05/1970 @ 12:00am (UTC).
export const UnixMondayFirst = 345600;

export const UnixMondayFirstStr = '1970-01-05T00:00:00Z';

// UnixMondaySecond is 01/12/1970 @ 12:00am (UTC).
export const UnixMondaySecond = 950400;

export const UnixMondaySecondStr = '1970-01-12T00:00:00Z';

// CurrentTimestamp returns the current timestamp as a number.
export const CurrentTimestamp = () =>
  Math.floor((new Date()).getTime() / 1000);

export const DurationStringFromSeconds = (sec) => {
  if (sec < 60) {
    return '0 minutes';
  }

  let days = '';
  let min = '';
  let hou = '';

  const dayNum = Math.floor(sec / DURATION_SECONDS.Day);
  let dayWord = 'day';
  if (dayNum > 1) {
    dayWord = 'days';
  }
  if (dayNum > 0) days = `${String(dayNum)} ${dayWord}`;

  const houNum = Math.floor(
    (sec - dayNum * DURATION_SECONDS.Day) / DURATION_SECONDS.Hour,
  );
  let houWord = 'hour';
  if (houNum > 1) {
    houWord = 'hours';
  }
  if (houNum > 0) hou = ` ${String(houNum)} ${houWord}`;

  const minNum = Math.floor((sec % DURATION_SECONDS.Hour) /
    DURATION_SECONDS.Minute);
  let minWord = 'minute';
  if (minNum > 1) {
    minWord = 'minutes';
  }
  if (minNum !== 0) {
    min = ` ${String(minNum)} ${minWord}`;
  }

  return `${days}${hou}${min}`;
};

// DurationString calculates a duration between two javascript millisecond
// timestamps and returns the duration as a neatly formatted string.
export const DurationString = (t1, t2) => {
  const sec = Math.abs(Math.floor((t2 - t1) / 1000));
  return DurationStringFromSeconds(sec);
};

// DurationHighestString calculates a duration between two javascript
// millisecond timestamps and returns the following given the following
// durations:
//  < 1 minute --> 'Less than a minute'
//  < 1 hour --> n many minutes
//  < 1 day --> n many hours
//  <= 31 days --> n many days
//  <= 56 days --> n many weeks, truncated
//  <= 365 days --> n many months, truncated
//  > 365 days --> n many years
export const DurationHighestString = (t1, t2) => {
  const sec = Math.abs(Math.floor((t2 - t1) / 1000));

  const cutoffDay = 31 * DURATION_SECONDS.Day;
  const cutoffWeek = 56 * DURATION_SECONDS.Day;
  const cutoffMonth = 365 * DURATION_SECONDS.Day;

  if (sec < DURATION_SECONDS.Minute) {
    return 'Less than a minute';
  }
  if (sec < DURATION_SECONDS.Hour) {
    const val = Math.round(sec / DURATION_SECONDS.Minute);
    return `${val} minute${val !== 1 ? 's' : ''}`;
  }
  if (sec < DURATION_SECONDS.Day) {
    const val = Math.round(sec / DURATION_SECONDS.Hour);
    return `${val} hour${val !== 1 ? 's' : ''}`;
  }
  if (sec <= cutoffDay) {
    const val = Math.round(sec / DURATION_SECONDS.Day);
    return `${val} day${val !== 1 ? 's' : ''}`;
  }
  if (sec <= cutoffWeek) {
    const val = Math.round(sec / DURATION_SECONDS.Week);
    return `${val} week${val !== 1 ? 's' : ''}`;
  }
  if (sec <= cutoffMonth) {
    const val = Math.round(sec / DURATION_SECONDS.Month);
    return `${val} month${val !== 1 ? 's' : ''}`;
  }

  const val = Math.round(sec / DURATION_SECONDS.Year);
  return `${val} year${val !== 1 ? 's' : ''}`;
};

// DurationOfTrainingRecord yields the duration of a training record from
// the created date to the expiration date.
export const DurationOfTrainingRecord = ({
  created = null,
  expires = null,
}) => {
  const start = moment(created).set({
    hour: 0,
    minute: 0,
    second: 0,
  });
  const end = moment(expires).set({
    hour: 0,
    minute: 0,
    second: 0,
  });

  const {
    years,
    months,
    days,
  } = moment.preciseDiff(start, end, true);
  let yearsAsStr = years ? `${years} year` : '';
  if (years > 1) yearsAsStr += 's';
  let monthsAsStr = months ? `${months} month` : '';
  if (months > 1) monthsAsStr += 's';
  let daysAsString = days ? `${days} day` : '';
  if (days > 1) daysAsString += 's';

  return _.reduce(
    [yearsAsStr, monthsAsStr, daysAsString],
    (sum, val, idx) => {
      if (val && sum === '') return `${val}`;
      if (val) return `${sum}, ${val}`;
      return sum;
    },
    '');
};

// MomentTz returns the correctly formatted moment time. It takes a UTC ISO8601
// formatted datetime string or unix timestamp and a timezone string as input.
export const MomentTz = (val, timezone, isUnix) => {
  let fn = moment.utc;
  if (isUnix) fn = (v) => moment.utc(moment.unix(v).toISOString());
  const m = fn(val);
  const a = m.toArray();

  return m.tz(timezone)
    .year(a[0])
    .month(a[1])
    .date(a[2])
    .hours(a[3])
    .minutes(a[4])
    .seconds(a[5])
    .milliseconds(a[6]);
};

// DateAndTimeString returns a pretty representation of a moment date.
export const DateAndTimeString = (mDate) => {
  const offset = Number(mDate.utcOffset());
  const dtLocal = mDate.clone().add(offset, 'minute');
  return `${dtLocal.format('ddd, MMM D YYYY')} ${dtLocal.format('h:mm a')}`;
};

// DateAndTimeStrings returns an array consisting of formatted strings
// for date and time, for both start dates and end dates. It also
// constructs a string for the duration. This results in an array such
// as this:
// [startDate, startTime, endDate, endTime, duration].
//
// We need the startAdj and endAdj because moment timezone format output for
// some zany reason sets whatever you output to local time despite registering
// the correct timezone. To fix this, we make a normal moment naive date
// tweaked to the correct timezone.
export const DateAndTimeStrings = ({
  dayMode = null,
  endUtc = null,
  startUtc = null,
  timezone = null,
}) => {
  const start = MomentTz(startUtc, timezone);
  const startOffset = start.utcOffset();
  const startAdj = moment(startUtc).utcOffset(startOffset);
  const end = MomentTz(endUtc, timezone);
  const endOffset = end.utcOffset();
  const endAdj = moment(endUtc).utcOffset(endOffset);

  const startDate = startAdj.format('ddd, MMM D YYYY');
  const startTime = startAdj.format('h:mm a');
  let endDate = endAdj.format('ddd, MMM D YYYY');
  if (dayMode) {
    endDate = endAdj.clone().add(-1, 'days').format('ddd, MMM D YYYY');
  }
  const endTime = endAdj.format('h:mm a');

  const duration = moment.preciseDiff(startAdj, endAdj);

  return [startDate, startTime, endDate, endTime, duration];
};

// UTC2Timezone takes a UTC datetime string and returns an
// array of the formatted date and time of the format:
// [dateString, timeString]
// Optionally, pass dateFmt and timeFmt parameters to customize
// the format of the outputted time.
export const UTC2SiteTz = (timeUTC, timezone, dateFmt, timeFmt) => {
  // Set default or overridden formats.
  let internalDateFmt = 'ddd, MMM D YYYY';
  let internalTimeFmt = 'h:mm a';
  if (dateFmt) internalDateFmt = dateFmt;
  if (timeFmt) internalTimeFmt = timeFmt;

  const time = MomentTz(timeUTC, timezone);
  const timeOffset = time.utcOffset();
  const timeAdj = moment(timeUTC).utcOffset(timeOffset);

  const dateString = timeAdj.format(internalDateFmt);
  const timeString = timeAdj.format(internalTimeFmt);

  return [dateString, timeString];
};

// DateTypeCheck checks to see if a DateTimePicker date object is in the
// correct form.
export const DateTypeCheck = (date) => {
  if (!_.isPlainObject(date)) {
    throw Error('DateTypeCheck failed: date must be an object');
  }
  if (_.isPlainObject(date) && !(date.dateMoment || date.dateString)) {
    throw Error('DateTypeCheck failed: date object must contain both ' +
      'moment and string properties');
  }
  if (!_.isNull(_.get(date, 'dateMoment._z', null))) {
    throw Error('DateTypeCheck failed: moment date must not be timezoned');
  }

  return date;
};

export const Sleep = (ms) => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

export const TimezoneStrip = (date) => {
  if (_.isUndefined(_.get(date, '_z'))) {
    throw Error('Can not strip timezone from non-moment date');
  }
  if (_.isNull(_.get(date, '_z', null))) {
    throw Error('Can not strip timezone when moment date has no timezone');
  }
  return moment({
    year: date.year(),
    month: date.month(),
    date: date.date(),
    hour: date.hour(),
    minute: date.minute(),
  });
};
